/**
 * Created by Ning Jiang on 2021-06-23.
 */

import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import {TombstoneRip} from "../../TombstoneRip";
import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {Layers, TombstoneRipLayers} from "../../view/TombstoneRipLayers";
import {gsap, TimelineLite} from "gsap";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {TombstoneRipGameAssets} from "../../TombstoneRipGameAssets";
import {MathHelper} from "../../../core/utils/MathHelper";
import {IBetWayWinData} from "../../server/data/ITombstoneRipData";

export class IndividualEffect extends TimelineSprite {

    public winLevel:number;

    constructor(level:number) {
        super(IndividualEffect.getLevelTextures(level), 30);
        this.winLevel = level;
        this.anchor.set(0.5);
    }

    private static getLevelTextures(level:number):PIXI.Texture[] {
        switch(level) {
            case 0:
            case 1:
                return TombstoneRip.assetLoader.getTextures(`individualWin/individualSmall/individualSmall_`);
            case 2:
                return TombstoneRip.assetLoader.getTextures(`individualWin/individualMid/individualMid_`);
            default:
                return TombstoneRip.assetLoader.getTextures(`individualWin/individualLarge/individualWin_`);
        }
    }
}

export class IndividualWinText extends PIXI.Container {
    private _maxWayWidth:number = 140;

    constructor(winAmount:number, way:number) {
        super();
        // winAmount = 100000000;
        // way = 11216;
        // winAmount = 10.22;
        // way = 122;
        const winNumberContainer = new PIXI.Container();
        this.addChild(winNumberContainer);
        winNumberContainer.y = -10;

        let winTotalWidth = 0;
        const winNumber = new PIXI.BitmapText(`${SlotGame.apiPlugin.currency.formatValue(winAmount)}`, {
            fontName : TombstoneRipGameAssets.BITMAP_FONT_WIN_COUNT_UP,
            fontSize : 20,
            tint : parseInt(("#ffffff").replace(/^#/, ''), 16)
        });
        winNumber.letterSpacing = -10;
        (<PIXI.Point>winNumber.anchor).set(0, 1.28);
        winNumberContainer.addChild(winNumber);
        winNumber.updateTransform();
        winTotalWidth += winNumber.textWidth;

        if(TombstoneRip.multiplier.currentMultiplier > 1) {
            const multiplier = new PIXI.BitmapText(`X${TombstoneRip.multiplier.currentMultiplier}`, {
                fontName : TombstoneRipGameAssets.BITMAP_FONT_MULTIPLIER,
                fontSize : 34,
                tint : 0xffffff
            });
            (<PIXI.Point>multiplier.anchor).set(1, 0.8);
            multiplier.letterSpacing = -12;
            winNumberContainer.addChild(multiplier);
            multiplier.updateTransform();
            winTotalWidth += multiplier.textWidth + 4;
            multiplier.x = winTotalWidth * 0.5;
        }
        winNumber.x = -winTotalWidth * 0.5;

        if(winTotalWidth > this._maxWayWidth) {
            const winScale = MathHelper.roundToDecimals(this._maxWayWidth / winTotalWidth, 2);
            winNumberContainer.scale.set(winScale);
        }

        const waysContainer = new PIXI.Container();
        this.addChild(waysContainer);
        waysContainer.y = 32;

        const waysWonNumber = new PIXI.BitmapText(way.toString(), {
            fontName : TombstoneRipGameAssets.BITMAP_FONT_MULTIPLIER,
            fontSize : 26,
            tint : 0xffffff
        });
        (<PIXI.Point>waysWonNumber.anchor).set(0, 0.6);
        waysWonNumber.letterSpacing = -10;
        waysContainer.addChild(waysWonNumber);

        const textureName:string = way === 1 ? 'initialwin/ways/wayWon' : 'initialwin/ways/waysWon';
        const waysWonText = new PIXI.Sprite(TombstoneRip.assetLoader.getTextures(textureName)[0]);
        waysWonText.anchor.set(1, 0.5);
        waysWonText.scale.set(0.24);
        waysContainer.addChild(waysWonText);

        waysWonNumber.updateTransform();
        const waysTotalWidth:number = waysWonNumber.textWidth + waysWonText.width;
        waysWonNumber.x = -waysTotalWidth / 2;
        waysWonText.x = waysTotalWidth / 2;
        if(waysTotalWidth > this._maxWayWidth) {
            const waysScale = MathHelper.roundToDecimals(this._maxWayWidth / waysTotalWidth, 2);
            waysContainer.scale.set(waysScale);
        }
    }
}

export class IndividualWinView {

    private _layer:StageLayer;
    private _effectPool:IndividualEffect[][] = [[], [], [], []];
    private _playingPool:IndividualEffect[] = [];
    private _winText:IndividualWinText | null;
    private _winTextTl:TweenLite | null;

    private _stopped:boolean;

    constructor() {
        this._layer = TombstoneRipLayers.getLayer(Layers.WIN_PRESENTATIONS)!;
    }

    public playAnimation(position:PIXI.Point, effectLevel:number, doubleReel:boolean, showWinText:boolean, winData?:IBetWayWinData):TimelineLite {
        const tl = new TimelineLite();

        if(doubleReel) {
            tl.add([
                this.playAnimation(new PIXI.Point(position.x, position.y - 92), effectLevel, false, showWinText, winData),
                this.playAnimation(new PIXI.Point(position.x, position.y + 92), effectLevel, false, false)
            ]);

            return tl;
        }

        if(showWinText && winData) {
            tl.add(() => {
                this.removeWinText();
                if(this._stopped) {
                    return;
                }
                this._winText = new IndividualWinText(MathHelper.roundToDecimals(winData.multipliedAmount / winData.multiplier, 2), winData.numberOfCombinations);
                this._winText.position.set(position.x, position.y);
                this._winText.alpha = 0;
                TombstoneRipLayers.getLayer(Layers.WIN_COUNT_UP)!.addChild(this._winText!);
                this._winTextTl = gsap.to(this._winText, {alpha : 1, duration : 0.1});
            }, 0);
        }

        const effect = this.getEffect(effectLevel);
        effect.position.set(position.x, position.y);
        effect.hide();
        this._playingPool.push(effect);

        tl.add([
            () => {
                if(!this._stopped) {
                    this._layer.addChild(effect);
                }
            },
            effect.getAnimationAutoShowHide(true, true)
        ], 0);
        tl.add(() => this.recycleEffect(effect));
        return tl;
    }

    private getEffect(effectLevel:number):IndividualEffect {
        if(this._effectPool[effectLevel].length > 0) {
            return this._effectPool[effectLevel].pop()!;
        }

        return new IndividualEffect(effectLevel);
    }

    private recycleEffect(effect:IndividualEffect):void {
        ArrayHelper.removeFirstMatchElement(this._playingPool, effect);
        this._layer.removeChild(effect);
        this._effectPool[effect.winLevel].push(effect);
    }

    public start():void {
        this._stopped = false;
    }

    public stop():void {
        this._stopped = true;
        this._playingPool.forEach(e => {
            e.hide();
        });
        this.removeWinText();
    }

    public removeWinText():void {
        if(this._winTextTl && this._winTextTl.isActive()) {
            this._winTextTl.progress(1);
        }
        this._winTextTl = null;

        if(this._winText) {
            if(this._winText.parent) {
                this._winText.parent.removeChild(this._winText);
            }
            this._winText.destroy();
            this._winText = null;
        }
    }
}